import { c as _c } from "react/compiler-runtime";
import Dropdown from '../Dropdown/Dropdown';
import React, { Fragment, type ReactElement, type ReactNode, useEffect, useMemo, useState } from 'react';
import { type MultiSelectDropdownItem, type MultiSelectDropdownProps } from './types';
import DropdownList from '../Dropdown/DropdownList';
import DropdownItem from '../Dropdown/DropdownItem';
import { ChevronDownIcon } from '../icons/ChevronDownIcon';
import { ChevronUpIcon } from '../icons/ChevronUpIcon';
import { RightIcon } from '../icons/RightIcon';
import { StyledAccordionSubOptionsContainer, StyledSubOptionsEmptyContent } from './styles';
import DropdownListSubCategory from '../Dropdown/DropdownListSubCategory';
import { groupOptionsBySubCategory } from '../Dropdown/DropdownListSubCategory/utils';
import Checkbox from '../Checkbox';
import Divider from '../Divider';
import Grid from '../Grid';
import Notification from '../Notification';
import MultiSelectButtonOpener from './MultiSelectButtonOpener';
import { buildSubCategoryGroupsWithEmptyContent } from './subCategoryEmptyContent';
import { getCategoryHeaderControlValue, getFullCategoryOptionsByLabel, getLeafOptions, getSelectAllHeaderControlValue, resolveCategoryOptionsForSelectAll, toggleCategorySelection } from './categorySelection';
const SUBMENU_GAP_PX = 2;
/** Matches Dropdown paper horizontal padding (`24px 12px`) + gap past the main panel edge. */
const SUBMENU_SIDE_OFFSET_PX = 12 + SUBMENU_GAP_PX;
/** Empty States needs a fixed column; searchable submenus can size within min/max. */
const EMPTY_SUBMENU_WIDTH_PX = 320;
const defaultIsOptionEqualToValue = (option: MultiSelectDropdownItem, value: string | MultiSelectDropdownItem): boolean => {
  if (typeof value === 'string') {
    return value === option.value;
  }
  return value.value === option.value && value.subCategory === option.subCategory;
};
const getParentSelectionBadgeValue = (option: MultiSelectDropdownItem, value: Array<string | MultiSelectDropdownItem>, isOptionEqualToValue: (option: MultiSelectDropdownItem, value: string | MultiSelectDropdownItem) => boolean): string | null => {
  if (option.subOptions == null || option.subOptions.length === 0) {
    return null;
  }
  const selectedCount = option.subOptions.filter(child => value.some(selectedValue => isOptionEqualToValue(child, selectedValue))).length;
  if (selectedCount === 0) {
    return null;
  }
  return selectedCount === option.subOptions.length ? 'all' : selectedCount.toString();
};
const SubOptionsAccordionSummary = t0 => {
  const $ = _c(15);
  const {
    option,
    isSubOptionExpanded,
    selectionBadgeValue
  } = t0;
  const t1 = option.leftIcon ?? option.icon;
  let t2;
  if ($[0] !== selectionBadgeValue) {
    t2 = selectionBadgeValue != null && <Notification variant="tonal" value={selectionBadgeValue} />;
    $[0] = selectionBadgeValue;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isSubOptionExpanded) {
    t3 = <Grid display="flex" width="32px" height="32px" alignItems="center" justifyContent="center">{isSubOptionExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}</Grid>;
    $[2] = isSubOptionExpanded;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <Grid display="flex" alignItems="center" columnGap="16px">{t2}{t3}</Grid>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== option.annotation || $[8] !== option.annotationLineType || $[9] !== option.disabled || $[10] !== option.label || $[11] !== option.tooltipProps || $[12] !== t1 || $[13] !== t4) {
    t5 = <DropdownItem onClick={_temp} annotation={option.annotation} annotationLineType={option.annotationLineType} leftIcon={t1} disabled={option.disabled} tooltipProps={option.tooltipProps} control={t4}>{option.label}</DropdownItem>;
    $[7] = option.annotation;
    $[8] = option.annotationLineType;
    $[9] = option.disabled;
    $[10] = option.label;
    $[11] = option.tooltipProps;
    $[12] = t1;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
const renderSubCategoryGroups = ({
  groups,
  renderOption,
  allowSelectAll = false,
  value,
  isOptionEqualToValue,
  onCategorySelectAll,
  fullCategoryOptionsByLabel
}: {
  groups: NonNullable<ReturnType<typeof buildSubCategoryGroupsWithEmptyContent<MultiSelectDropdownItem>>>;
  renderOption: (option: MultiSelectDropdownItem) => ReactElement;
  allowSelectAll?: boolean;
  value?: Array<string | MultiSelectDropdownItem>;
  isOptionEqualToValue?: (option: MultiSelectDropdownItem, value: string | MultiSelectDropdownItem) => boolean;
  onCategorySelectAll?: (categoryOptions: MultiSelectDropdownItem[]) => void;
  fullCategoryOptionsByLabel?: ReadonlyMap<string, MultiSelectDropdownItem[]>;
}): ReactElement[] => groups.map(({
  label,
  avatar,
  options: categoryOptions,
  emptyContent
}, index) => {
  const categoryOptionsForSelectAll = resolveCategoryOptionsForSelectAll(label, categoryOptions, fullCategoryOptionsByLabel);
  const showCategorySelectAll = allowSelectAll && categoryOptions.length > 0 && value != null && isOptionEqualToValue != null && onCategorySelectAll != null;
  const headerControlValue = showCategorySelectAll ? getCategoryHeaderControlValue(categoryOptionsForSelectAll, value, isOptionEqualToValue) : undefined;
  return <Fragment key={label}>
        {index !== 0 && <Divider />}
        <DropdownListSubCategory header={label} avatar={avatar} headerControlValue={headerControlValue} headerControlOnChange={showCategorySelectAll ? () => {
      onCategorySelectAll(categoryOptionsForSelectAll);
    } : undefined}>
          {categoryOptions.length === 0 && emptyContent != null ? <StyledSubOptionsEmptyContent role="presentation">{emptyContent}</StyledSubOptionsEmptyContent> : categoryOptions.map(renderOption)}
        </DropdownListSubCategory>
      </Fragment>;
});
const renderSubmenuListBody = ({
  showParentEmptyContent,
  parentEmptyContent,
  submenuSubCategoryGroups,
  filteredSubOptions,
  renderSubOption,
  allowSelectAll,
  value,
  isOptionEqualToValue,
  onCategorySelectAll,
  fullCategoryOptionsByLabel
}: {
  showParentEmptyContent: boolean;
  parentEmptyContent: ReactNode;
  submenuSubCategoryGroups: ReturnType<typeof buildSubCategoryGroupsWithEmptyContent<MultiSelectDropdownItem>>;
  filteredSubOptions: MultiSelectDropdownItem[];
  renderSubOption: (option: MultiSelectDropdownItem) => ReactElement;
  allowSelectAll?: boolean;
  value?: Array<string | MultiSelectDropdownItem>;
  isOptionEqualToValue?: (option: MultiSelectDropdownItem, value: string | MultiSelectDropdownItem) => boolean;
  onCategorySelectAll?: (categoryOptions: MultiSelectDropdownItem[]) => void;
  fullCategoryOptionsByLabel?: ReadonlyMap<string, MultiSelectDropdownItem[]>;
}): ReactNode => {
  if (showParentEmptyContent) {
    return <StyledSubOptionsEmptyContent role="presentation">{parentEmptyContent}</StyledSubOptionsEmptyContent>;
  }
  if (submenuSubCategoryGroups != null) {
    return renderSubCategoryGroups({
      groups: submenuSubCategoryGroups,
      renderOption: renderSubOption,
      allowSelectAll,
      value,
      isOptionEqualToValue,
      onCategorySelectAll,
      fullCategoryOptionsByLabel
    });
  }
  return filteredSubOptions.map(renderSubOption);
};
const DropdownOption = t0 => {
  const $ = _c(132);
  const {
    option,
    isSelected,
    handleOptionClick,
    isSubOptionExpanded,
    subOptionsVariant: t1,
    value,
    isOptionEqualToValue,
    onToggleSubOptions,
    onCloseSubOptions,
    subOptionsEmptyContent,
    subCategoryEmptyContent,
    allowSelectAll: t2,
    onCategorySelectAll,
    indeterminateValues
  } = t0;
  const subOptionsVariant = t1 === undefined ? "accordion" : t1;
  const allowSelectAll = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== indeterminateValues || $[1] !== option.value) {
    t3 = indeterminateValues?.has(option.value);
    $[0] = indeterminateValues;
    $[1] = option.value;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const isIndeterminate = t3 === true;
  const hasSubOptions = option.subOptions != null;
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [submenuSearchValue, setSubmenuSearchValue] = useState(undefined);
  let t4;
  let t5;
  if ($[3] !== isSubOptionExpanded) {
    t4 = () => {
      if (!isSubOptionExpanded) {
        setSubmenuSearchValue(undefined);
      }
    };
    t5 = [isSubOptionExpanded];
    $[3] = isSubOptionExpanded;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  bb0: {
    if (option.subOptions == null) {
      let t7;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = [];
        $[6] = t7;
      } else {
        t7 = $[6];
      }
      t6 = t7;
      break bb0;
    }
    if (submenuSearchValue == null || submenuSearchValue === "") {
      t6 = option.subOptions;
      break bb0;
    }
    let t7;
    if ($[7] !== option.subOptions || $[8] !== submenuSearchValue) {
      let t8;
      if ($[10] !== submenuSearchValue) {
        t8 = subOption => subOption.label.toLowerCase().includes(submenuSearchValue.toLowerCase());
        $[10] = submenuSearchValue;
        $[11] = t8;
      } else {
        t8 = $[11];
      }
      t7 = option.subOptions.filter(t8);
      $[7] = option.subOptions;
      $[8] = submenuSearchValue;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    t6 = t7;
  }
  const filteredSubOptions = t6;
  const parentSubCategoryEmptyContent = subCategoryEmptyContent?.[option.value];
  let t7;
  if ($[12] !== filteredSubOptions || $[13] !== parentSubCategoryEmptyContent) {
    t7 = buildSubCategoryGroupsWithEmptyContent(filteredSubOptions, parentSubCategoryEmptyContent);
    $[12] = filteredSubOptions;
    $[13] = parentSubCategoryEmptyContent;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const submenuSubCategoryGroups = t7;
  let t8;
  if ($[15] !== option.subOptions || $[16] !== parentSubCategoryEmptyContent) {
    t8 = getFullCategoryOptionsByLabel(buildSubCategoryGroupsWithEmptyContent(option.subOptions ?? [], parentSubCategoryEmptyContent));
    $[15] = option.subOptions;
    $[16] = parentSubCategoryEmptyContent;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const fullCategoryOptionsByLabel = t8;
  let t9;
  if ($[18] !== isOptionEqualToValue || $[19] !== option || $[20] !== value) {
    t9 = getParentSelectionBadgeValue(option, value, isOptionEqualToValue);
    $[18] = isOptionEqualToValue;
    $[19] = option;
    $[20] = value;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const selectionBadgeValue = t9;
  const isSubmenuOpen = isSubOptionExpanded && submenuAnchorEl != null;
  const parentEmptyContent = subOptionsEmptyContent?.[option.value];
  const showParentEmptyContent = parentEmptyContent != null && (option.subOptions?.length ?? 0) === 0;
  if (hasSubOptions && subOptionsVariant === "submenu") {
    const submenuSearchPlaceholder = option.label;
    let t10;
    if ($[22] !== allowSelectAll || $[23] !== handleOptionClick || $[24] !== indeterminateValues || $[25] !== isOptionEqualToValue || $[26] !== onCategorySelectAll || $[27] !== onCloseSubOptions || $[28] !== onToggleSubOptions || $[29] !== subCategoryEmptyContent || $[30] !== subOptionsEmptyContent || $[31] !== subOptionsVariant || $[32] !== value) {
      t10 = subOption_0 => {
        const isSubSelected = value.some(valueItem => isOptionEqualToValue(subOption_0, valueItem));
        return <DropdownOption key={subOption_0.value} option={subOption_0} isSelected={isSubSelected} handleOptionClick={handleOptionClick} isSubOptionExpanded={false} subOptionsVariant={subOptionsVariant} value={value} isOptionEqualToValue={isOptionEqualToValue} onToggleSubOptions={onToggleSubOptions} onCloseSubOptions={onCloseSubOptions} subOptionsEmptyContent={subOptionsEmptyContent} subCategoryEmptyContent={subCategoryEmptyContent} allowSelectAll={allowSelectAll} onCategorySelectAll={onCategorySelectAll} indeterminateValues={indeterminateValues} />;
      };
      $[22] = allowSelectAll;
      $[23] = handleOptionClick;
      $[24] = indeterminateValues;
      $[25] = isOptionEqualToValue;
      $[26] = onCategorySelectAll;
      $[27] = onCloseSubOptions;
      $[28] = onToggleSubOptions;
      $[29] = subCategoryEmptyContent;
      $[30] = subOptionsEmptyContent;
      $[31] = subOptionsVariant;
      $[32] = value;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    const renderSubOption = t10;
    let t11;
    if ($[34] !== onToggleSubOptions || $[35] !== option.value) {
      t11 = () => {
        onToggleSubOptions(option.value);
      };
      $[34] = onToggleSubOptions;
      $[35] = option.value;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    const t12 = option.leftIcon ?? option.icon;
    let t13;
    if ($[37] !== selectionBadgeValue) {
      t13 = selectionBadgeValue != null && <Notification variant="tonal" value={selectionBadgeValue} />;
      $[37] = selectionBadgeValue;
      $[38] = t13;
    } else {
      t13 = $[38];
    }
    let t14;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <Grid display="flex" width="32px" height="32px" alignItems="center" justifyContent="center"><RightIcon /></Grid>;
      $[39] = t14;
    } else {
      t14 = $[39];
    }
    let t15;
    if ($[40] !== t13) {
      t15 = <Grid display="flex" alignItems="center" columnGap="16px">{t13}{t14}</Grid>;
      $[40] = t13;
      $[41] = t15;
    } else {
      t15 = $[41];
    }
    let t16;
    if ($[42] !== isSubOptionExpanded || $[43] !== option.annotation || $[44] !== option.annotationLineType || $[45] !== option.disabled || $[46] !== option.label || $[47] !== option.tooltipProps || $[48] !== t11 || $[49] !== t12 || $[50] !== t15) {
      t16 = <DropdownItem ref={setSubmenuAnchorEl} onClick={t11} annotation={option.annotation} annotationLineType={option.annotationLineType} leftIcon={t12} disabled={option.disabled} tooltipProps={option.tooltipProps} isActive={isSubOptionExpanded} control={t15}>{option.label}</DropdownItem>;
      $[42] = isSubOptionExpanded;
      $[43] = option.annotation;
      $[44] = option.annotationLineType;
      $[45] = option.disabled;
      $[46] = option.label;
      $[47] = option.tooltipProps;
      $[48] = t11;
      $[49] = t12;
      $[50] = t15;
      $[51] = t16;
    } else {
      t16 = $[51];
    }
    let t17;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = [{
        name: "offset",
        options: {
          offset: _temp2
        }
      }];
      $[52] = t17;
    } else {
      t17 = $[52];
    }
    let t18;
    if ($[53] !== onCloseSubOptions) {
      t18 = () => {
        setSubmenuSearchValue(undefined);
        onCloseSubOptions();
      };
      $[53] = onCloseSubOptions;
      $[54] = t18;
    } else {
      t18 = $[54];
    }
    const t19 = showParentEmptyContent ? EMPTY_SUBMENU_WIDTH_PX : undefined;
    let t20;
    if ($[55] !== showParentEmptyContent || $[56] !== submenuSearchPlaceholder || $[57] !== submenuSearchValue) {
      t20 = showParentEmptyContent ? undefined : {
        value: submenuSearchValue,
        onChange: setSubmenuSearchValue,
        placeholder: submenuSearchPlaceholder
      };
      $[55] = showParentEmptyContent;
      $[56] = submenuSearchPlaceholder;
      $[57] = submenuSearchValue;
      $[58] = t20;
    } else {
      t20 = $[58];
    }
    let t21;
    if ($[59] !== allowSelectAll || $[60] !== filteredSubOptions || $[61] !== fullCategoryOptionsByLabel || $[62] !== isOptionEqualToValue || $[63] !== onCategorySelectAll || $[64] !== parentEmptyContent || $[65] !== renderSubOption || $[66] !== showParentEmptyContent || $[67] !== submenuSubCategoryGroups || $[68] !== value) {
      t21 = renderSubmenuListBody({
        showParentEmptyContent,
        parentEmptyContent,
        submenuSubCategoryGroups,
        filteredSubOptions,
        renderSubOption,
        allowSelectAll,
        value,
        isOptionEqualToValue,
        onCategorySelectAll,
        fullCategoryOptionsByLabel
      });
      $[59] = allowSelectAll;
      $[60] = filteredSubOptions;
      $[61] = fullCategoryOptionsByLabel;
      $[62] = isOptionEqualToValue;
      $[63] = onCategorySelectAll;
      $[64] = parentEmptyContent;
      $[65] = renderSubOption;
      $[66] = showParentEmptyContent;
      $[67] = submenuSubCategoryGroups;
      $[68] = value;
      $[69] = t21;
    } else {
      t21 = $[69];
    }
    let t22;
    if ($[70] !== t19 || $[71] !== t20 || $[72] !== t21) {
      t22 = <DropdownList dense={true} minWidth={EMPTY_SUBMENU_WIDTH_PX} maxWidth={EMPTY_SUBMENU_WIDTH_PX} width={t19} searchbar={t20}>{t21}</DropdownList>;
      $[70] = t19;
      $[71] = t20;
      $[72] = t21;
      $[73] = t22;
    } else {
      t22 = $[73];
    }
    let t23;
    if ($[74] !== isSubmenuOpen || $[75] !== submenuAnchorEl || $[76] !== t18 || $[77] !== t22) {
      t23 = <Dropdown open={isSubmenuOpen} anchorEl={submenuAnchorEl} placement="right-start" modifiers={t17} onClose={t18}>{t22}</Dropdown>;
      $[74] = isSubmenuOpen;
      $[75] = submenuAnchorEl;
      $[76] = t18;
      $[77] = t22;
      $[78] = t23;
    } else {
      t23 = $[78];
    }
    let t24;
    if ($[79] !== t16 || $[80] !== t23) {
      t24 = <>{t16}{t23}</>;
      $[79] = t16;
      $[80] = t23;
      $[81] = t24;
    } else {
      t24 = $[81];
    }
    return t24;
  }
  if (hasSubOptions) {
    let t10;
    if ($[82] !== isSubOptionExpanded || $[83] !== option || $[84] !== selectionBadgeValue) {
      t10 = <SubOptionsAccordionSummary option={option} isSubOptionExpanded={isSubOptionExpanded} selectionBadgeValue={selectionBadgeValue} />;
      $[82] = isSubOptionExpanded;
      $[83] = option;
      $[84] = selectionBadgeValue;
      $[85] = t10;
    } else {
      t10 = $[85];
    }
    let t11;
    if ($[86] !== allowSelectAll || $[87] !== handleOptionClick || $[88] !== indeterminateValues || $[89] !== isOptionEqualToValue || $[90] !== onCategorySelectAll || $[91] !== onCloseSubOptions || $[92] !== onToggleSubOptions || $[93] !== option.subOptions || $[94] !== subCategoryEmptyContent || $[95] !== subOptionsEmptyContent || $[96] !== value) {
      t11 = option.subOptions?.map(subOption_1 => {
        const isSubSelected_0 = value.some(valueItem_0 => isOptionEqualToValue(subOption_1, valueItem_0));
        return <DropdownOption key={subOption_1.value} option={subOption_1} isSelected={isSubSelected_0} handleOptionClick={handleOptionClick} isSubOptionExpanded={false} value={value} isOptionEqualToValue={isOptionEqualToValue} onToggleSubOptions={onToggleSubOptions} onCloseSubOptions={onCloseSubOptions} subOptionsEmptyContent={subOptionsEmptyContent} subCategoryEmptyContent={subCategoryEmptyContent} allowSelectAll={allowSelectAll} onCategorySelectAll={onCategorySelectAll} indeterminateValues={indeterminateValues} />;
      });
      $[86] = allowSelectAll;
      $[87] = handleOptionClick;
      $[88] = indeterminateValues;
      $[89] = isOptionEqualToValue;
      $[90] = onCategorySelectAll;
      $[91] = onCloseSubOptions;
      $[92] = onToggleSubOptions;
      $[93] = option.subOptions;
      $[94] = subCategoryEmptyContent;
      $[95] = subOptionsEmptyContent;
      $[96] = value;
      $[97] = t11;
    } else {
      t11 = $[97];
    }
    let t12;
    if ($[98] !== onCloseSubOptions || $[99] !== onToggleSubOptions || $[100] !== option.value) {
      t12 = (_, expanded) => {
        if (expanded) {
          onToggleSubOptions(option.value);
        } else {
          onCloseSubOptions();
        }
      };
      $[98] = onCloseSubOptions;
      $[99] = onToggleSubOptions;
      $[100] = option.value;
      $[101] = t12;
    } else {
      t12 = $[101];
    }
    let t13;
    if ($[102] !== isSubOptionExpanded || $[103] !== t10 || $[104] !== t11 || $[105] !== t12) {
      t13 = <StyledAccordionSubOptionsContainer Summary={t10} Details={t11} expanded={isSubOptionExpanded} onChange={t12} />;
      $[102] = isSubOptionExpanded;
      $[103] = t10;
      $[104] = t11;
      $[105] = t12;
      $[106] = t13;
    } else {
      t13 = $[106];
    }
    return t13;
  }
  let t10;
  if ($[107] !== handleOptionClick || $[108] !== isSelected || $[109] !== option) {
    t10 = () => {
      handleOptionClick(option, isSelected);
    };
    $[107] = handleOptionClick;
    $[108] = isSelected;
    $[109] = option;
    $[110] = t10;
  } else {
    t10 = $[110];
  }
  let t11;
  if ($[111] !== option.count) {
    t11 = typeof option.count === "number" && <Notification variant="tonal" value={option.count.toString()} />;
    $[111] = option.count;
    $[112] = t11;
  } else {
    t11 = $[112];
  }
  const t12 = isSelected && !isIndeterminate;
  let t13;
  if ($[113] !== isIndeterminate || $[114] !== option.disabled || $[115] !== t12) {
    t13 = <Checkbox checked={t12} indeterminate={isIndeterminate} disabled={option.disabled} />;
    $[113] = isIndeterminate;
    $[114] = option.disabled;
    $[115] = t12;
    $[116] = t13;
  } else {
    t13 = $[116];
  }
  let t14;
  if ($[117] !== t11 || $[118] !== t13) {
    t14 = <Grid display="flex" alignItems="center" columnGap="16px">{t11}{t13}</Grid>;
    $[117] = t11;
    $[118] = t13;
    $[119] = t14;
  } else {
    t14 = $[119];
  }
  const t15 = option.leftIcon ?? option.icon;
  let t16;
  if ($[120] !== option.annotation || $[121] !== option.annotationLineType || $[122] !== option.customContent || $[123] !== option.disabled || $[124] !== option.label || $[125] !== option.rightIcon || $[126] !== option.tooltipProps || $[127] !== option.value || $[128] !== t10 || $[129] !== t14 || $[130] !== t15) {
    t16 = <DropdownItem key={option.value} onClick={t10} control={t14} annotation={option.annotation} annotationLineType={option.annotationLineType} leftIcon={t15} disabled={option.disabled} tooltipProps={option.tooltipProps} rightIcon={option.rightIcon} customContent={option.customContent}>{option.label}</DropdownItem>;
    $[120] = option.annotation;
    $[121] = option.annotationLineType;
    $[122] = option.customContent;
    $[123] = option.disabled;
    $[124] = option.label;
    $[125] = option.rightIcon;
    $[126] = option.tooltipProps;
    $[127] = option.value;
    $[128] = t10;
    $[129] = t14;
    $[130] = t15;
    $[131] = t16;
  } else {
    t16 = $[131];
  }
  return t16;
};
const MultiSelectDropdown = t0 => {
  const $ = _c(126);
  const {
    title,
    buttonTitle,
    value: t1,
    options,
    onChange,
    onClose,
    disabled: t2,
    buttonWidth,
    dropdownMinWidth: t3,
    allowSelectAll: t4,
    isOptionEqualToValue: t5,
    CustomAnchorElement,
    includeSearch: t6,
    searchFilterFunction,
    customAnchorElementProps,
    count,
    selectAllLabel,
    indeterminateValues: t7,
    fixedPositionOnOpen: t8,
    subOptionsVariant: t9,
    subOptionsEmptyContent,
    subCategoryEmptyContent,
    ref
  } = t0;
  let t10;
  if ($[0] !== t1) {
    t10 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const value = t10;
  const disabled = t2 === undefined ? false : t2;
  const dropdownMinWidth = t3 === undefined ? "297px" : t3;
  const allowSelectAll = t4 === undefined ? true : t4;
  const isOptionEqualToValue = t5 === undefined ? defaultIsOptionEqualToValue : t5;
  const includeSearch = t6 === undefined ? false : t6;
  let t11;
  if ($[2] !== t7) {
    t11 = t7 === undefined ? [] : t7;
    $[2] = t7;
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  const indeterminateValues = t11;
  const fixedPositionOnOpen = t8 === undefined ? false : t8;
  const subOptionsVariant = t9 === undefined ? "accordion" : t9;
  let t12;
  if ($[4] !== options) {
    t12 = getLeafOptions(options);
    $[4] = options;
    $[5] = t12;
  } else {
    t12 = $[5];
  }
  const leafOptions = t12;
  let t13;
  if ($[6] !== indeterminateValues) {
    t13 = new Set(indeterminateValues);
    $[6] = indeterminateValues;
    $[7] = t13;
  } else {
    t13 = $[7];
  }
  const indeterminateValueSet = t13;
  let t14;
  if ($[8] !== isOptionEqualToValue || $[9] !== leafOptions || $[10] !== value) {
    let t15;
    if ($[12] !== isOptionEqualToValue || $[13] !== value) {
      t15 = option => {
        if (option.disabled !== true) {
          return false;
        }
        return value.some(selectedValue => isOptionEqualToValue(option, selectedValue));
      };
      $[12] = isOptionEqualToValue;
      $[13] = value;
      $[14] = t15;
    } else {
      t15 = $[14];
    }
    t14 = leafOptions.filter(t15);
    $[8] = isOptionEqualToValue;
    $[9] = leafOptions;
    $[10] = value;
    $[11] = t14;
  } else {
    t14 = $[11];
  }
  const lockedOptions = t14;
  let t15;
  if ($[15] !== isOptionEqualToValue || $[16] !== leafOptions || $[17] !== value) {
    let t16;
    if ($[19] !== isOptionEqualToValue || $[20] !== value) {
      t16 = option_0 => {
        if (option_0.disabled !== true) {
          return true;
        }
        if (value.some(selectedValue_0 => isOptionEqualToValue(option_0, selectedValue_0))) {
          return true;
        }
        return false;
      };
      $[19] = isOptionEqualToValue;
      $[20] = value;
      $[21] = t16;
    } else {
      t16 = $[21];
    }
    t15 = leafOptions.filter(t16);
    $[15] = isOptionEqualToValue;
    $[16] = leafOptions;
    $[17] = value;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  const enabledOptions = t15;
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState(undefined);
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      top: 0,
      left: 0
    };
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const [anchorPosition, setAnchorPosition] = useState(t16);
  const [openParentValue, setOpenParentValue] = useState(null);
  let t17;
  if ($[23] !== fixedPositionOnOpen) {
    t17 = event => {
      setDropdownAnchorEl(event.currentTarget);
      if (fixedPositionOnOpen) {
        const rect = event.currentTarget.getBoundingClientRect();
        setAnchorPosition({
          top: rect.bottom,
          left: rect.left
        });
      }
    };
    $[23] = fixedPositionOnOpen;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const handleButtonClick = t17;
  let t18;
  if ($[25] !== onClose) {
    t18 = () => {
      setDropdownAnchorEl(null);
      setSearchValue(undefined);
      setOpenParentValue(null);
      onClose?.();
    };
    $[25] = onClose;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const handleClose = t18;
  let t19;
  if ($[27] !== handleClose || $[28] !== openParentValue || $[29] !== subOptionsVariant) {
    t19 = (_event, reason) => {
      if (subOptionsVariant === "submenu" && openParentValue != null && reason === "escapeKeyDown") {
        setOpenParentValue(null);
        return;
      }
      handleClose();
    };
    $[27] = handleClose;
    $[28] = openParentValue;
    $[29] = subOptionsVariant;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  const handleDropdownClose = t19;
  let t20;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      setOpenParentValue(null);
    };
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  const handleCloseSubOptions = t20;
  let t21;
  if ($[32] !== subOptionsVariant) {
    t21 = optionValue => {
      setOpenParentValue(current => {
        if (subOptionsVariant === "accordion") {
          return optionValue;
        }
        return current === optionValue ? null : optionValue;
      });
      if (subOptionsVariant === "accordion") {
        setTimeout(_temp3, 50);
      }
    };
    $[32] = subOptionsVariant;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  const handleToggleSubOptions = t21;
  let t22;
  if ($[34] !== isOptionEqualToValue || $[35] !== leafOptions || $[36] !== onChange || $[37] !== value) {
    t22 = (optionValue_0, isSelected) => {
      let newValues = leafOptions.filter(option_1 => value.some(selectedValue_1 => isOptionEqualToValue(option_1, selectedValue_1)));
      if (isSelected) {
        newValues = newValues.filter(selectedValue_2 => !isOptionEqualToValue(selectedValue_2, optionValue_0));
      } else {
        newValues = [...newValues, optionValue_0];
      }
      onChange(optionValue_0.value, newValues);
    };
    $[34] = isOptionEqualToValue;
    $[35] = leafOptions;
    $[36] = onChange;
    $[37] = value;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  const handleOptionClick = t22;
  let t23;
  if ($[39] !== isOptionEqualToValue || $[40] !== leafOptions || $[41] !== onChange || $[42] !== value) {
    t23 = categoryOptions => {
      toggleCategorySelection({
        categoryOptions,
        leafOptions,
        value,
        isOptionEqualToValue,
        onChange
      });
    };
    $[39] = isOptionEqualToValue;
    $[40] = leafOptions;
    $[41] = onChange;
    $[42] = value;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  const handleCategorySelectAll = t23;
  let t24;
  if ($[44] !== enabledOptions || $[45] !== indeterminateValueSet || $[46] !== value) {
    t24 = getSelectAllHeaderControlValue(enabledOptions, value, indeterminateValueSet);
    $[44] = enabledOptions;
    $[45] = indeterminateValueSet;
    $[46] = value;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  const headerControlValue = t24;
  let t25;
  if ($[48] !== enabledOptions || $[49] !== headerControlValue || $[50] !== lockedOptions || $[51] !== onChange) {
    t25 = () => {
      if (headerControlValue === "checked") {
        onChange(null, lockedOptions);
      } else {
        onChange(null, enabledOptions);
      }
    };
    $[48] = enabledOptions;
    $[49] = headerControlValue;
    $[50] = lockedOptions;
    $[51] = onChange;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  const handleHeaderControlChange = t25;
  let t26;
  bb0: {
    if (searchValue == null || searchValue === "") {
      t26 = options;
      break bb0;
    }
    if (searchFilterFunction != null) {
      let t27;
      if ($[53] !== options || $[54] !== searchFilterFunction || $[55] !== searchValue) {
        t27 = searchFilterFunction(options, searchValue);
        $[53] = options;
        $[54] = searchFilterFunction;
        $[55] = searchValue;
        $[56] = t27;
      } else {
        t27 = $[56];
      }
      t26 = t27;
      break bb0;
    }
    let t27;
    if ($[57] !== options || $[58] !== searchValue) {
      let t28;
      if ($[60] !== searchValue) {
        t28 = option_2 => option_2.label.toLowerCase().includes(searchValue.toLowerCase());
        $[60] = searchValue;
        $[61] = t28;
      } else {
        t28 = $[61];
      }
      t27 = options.filter(t28);
      $[57] = options;
      $[58] = searchValue;
      $[59] = t27;
    } else {
      t27 = $[59];
    }
    t26 = t27;
  }
  const filteredOptions = t26;
  let t27;
  if ($[62] !== filteredOptions) {
    t27 = groupOptionsBySubCategory(filteredOptions, true);
    $[62] = filteredOptions;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  const subCategoryGroups = t27;
  let t28;
  if ($[64] !== options) {
    t28 = getFullCategoryOptionsByLabel(groupOptionsBySubCategory(options, true));
    $[64] = options;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  const fullCategoryOptionsByLabel = t28;
  const isDropdownOpen = dropdownAnchorEl != null;
  let t29;
  if ($[66] !== allowSelectAll || $[67] !== handleCategorySelectAll || $[68] !== handleOptionClick || $[69] !== handleToggleSubOptions || $[70] !== indeterminateValueSet || $[71] !== isOptionEqualToValue || $[72] !== openParentValue || $[73] !== subCategoryEmptyContent || $[74] !== subOptionsEmptyContent || $[75] !== subOptionsVariant || $[76] !== value) {
    t29 = option_3 => <DropdownOption key={option_3.value} option={option_3} isSelected={value.some(valueItem => isOptionEqualToValue(option_3, valueItem))} handleOptionClick={handleOptionClick} isSubOptionExpanded={openParentValue === option_3.value} subOptionsVariant={subOptionsVariant} value={value} isOptionEqualToValue={isOptionEqualToValue} onToggleSubOptions={handleToggleSubOptions} onCloseSubOptions={handleCloseSubOptions} subOptionsEmptyContent={subOptionsEmptyContent} subCategoryEmptyContent={subCategoryEmptyContent} allowSelectAll={allowSelectAll} onCategorySelectAll={handleCategorySelectAll} indeterminateValues={indeterminateValueSet} />;
    $[66] = allowSelectAll;
    $[67] = handleCategorySelectAll;
    $[68] = handleOptionClick;
    $[69] = handleToggleSubOptions;
    $[70] = indeterminateValueSet;
    $[71] = isOptionEqualToValue;
    $[72] = openParentValue;
    $[73] = subCategoryEmptyContent;
    $[74] = subOptionsEmptyContent;
    $[75] = subOptionsVariant;
    $[76] = value;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  const renderTopLevelOption = t29;
  let t30;
  if ($[78] !== CustomAnchorElement || $[79] !== buttonTitle || $[80] !== buttonWidth || $[81] !== customAnchorElementProps || $[82] !== disabled || $[83] !== handleButtonClick || $[84] !== isDropdownOpen || $[85] !== leafOptions || $[86] !== ref || $[87] !== value) {
    t30 = CustomAnchorElement == null ? <MultiSelectButtonOpener ref={ref} buttonTitle={buttonTitle} buttonWidth={buttonWidth} options={leafOptions} value={value} isDropdownOpen={isDropdownOpen} disabled={disabled} onClick={handleButtonClick} /> : <CustomAnchorElement {...customAnchorElementProps} onClick={handleButtonClick} isDropdownOpen={isDropdownOpen} />;
    $[78] = CustomAnchorElement;
    $[79] = buttonTitle;
    $[80] = buttonWidth;
    $[81] = customAnchorElementProps;
    $[82] = disabled;
    $[83] = handleButtonClick;
    $[84] = isDropdownOpen;
    $[85] = leafOptions;
    $[86] = ref;
    $[87] = value;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  const t31 = fixedPositionOnOpen ? anchorPosition : undefined;
  const t32 = fixedPositionOnOpen ? "anchorPosition" : "anchorEl";
  const t33 = includeSearch ? 372 : dropdownMinWidth;
  const t34 = includeSearch ? 372 : undefined;
  let t35;
  if ($[89] !== allowSelectAll || $[90] !== handleHeaderControlChange || $[91] !== headerControlValue || $[92] !== includeSearch || $[93] !== selectAllLabel) {
    t35 = !includeSearch && {
      headerControlTitle: selectAllLabel,
      headerControlValue: allowSelectAll ? headerControlValue : undefined,
      headerControlOnChange: allowSelectAll ? handleHeaderControlChange : undefined
    };
    $[89] = allowSelectAll;
    $[90] = handleHeaderControlChange;
    $[91] = headerControlValue;
    $[92] = includeSearch;
    $[93] = selectAllLabel;
    $[94] = t35;
  } else {
    t35 = $[94];
  }
  let t36;
  if ($[95] !== includeSearch || $[96] !== searchValue || $[97] !== title) {
    t36 = includeSearch ? {
      value: searchValue,
      onChange: setSearchValue,
      placeholder: title
    } : undefined;
    $[95] = includeSearch;
    $[96] = searchValue;
    $[97] = title;
    $[98] = t36;
  } else {
    t36 = $[98];
  }
  let t37;
  if ($[99] !== allowSelectAll || $[100] !== filteredOptions || $[101] !== fullCategoryOptionsByLabel || $[102] !== handleCategorySelectAll || $[103] !== isOptionEqualToValue || $[104] !== renderTopLevelOption || $[105] !== subCategoryGroups || $[106] !== value) {
    t37 = subCategoryGroups != null ? renderSubCategoryGroups({
      groups: subCategoryGroups,
      renderOption: renderTopLevelOption,
      allowSelectAll,
      value,
      isOptionEqualToValue,
      onCategorySelectAll: handleCategorySelectAll,
      fullCategoryOptionsByLabel
    }) : filteredOptions.map(renderTopLevelOption);
    $[99] = allowSelectAll;
    $[100] = filteredOptions;
    $[101] = fullCategoryOptionsByLabel;
    $[102] = handleCategorySelectAll;
    $[103] = isOptionEqualToValue;
    $[104] = renderTopLevelOption;
    $[105] = subCategoryGroups;
    $[106] = value;
    $[107] = t37;
  } else {
    t37 = $[107];
  }
  let t38;
  if ($[108] !== count || $[109] !== t33 || $[110] !== t34 || $[111] !== t35 || $[112] !== t36 || $[113] !== t37 || $[114] !== title) {
    t38 = <DropdownList dense={true} minWidth={t33} maxWidth={t34} header={title} {...t35} count={count} searchbar={t36}>{t37}</DropdownList>;
    $[108] = count;
    $[109] = t33;
    $[110] = t34;
    $[111] = t35;
    $[112] = t36;
    $[113] = t37;
    $[114] = title;
    $[115] = t38;
  } else {
    t38 = $[115];
  }
  let t39;
  if ($[116] !== dropdownAnchorEl || $[117] !== handleDropdownClose || $[118] !== isDropdownOpen || $[119] !== t31 || $[120] !== t32 || $[121] !== t38) {
    t39 = <Dropdown anchorEl={dropdownAnchorEl} open={isDropdownOpen} onClose={handleDropdownClose} anchorPosition={t31} anchorReference={t32}>{t38}</Dropdown>;
    $[116] = dropdownAnchorEl;
    $[117] = handleDropdownClose;
    $[118] = isDropdownOpen;
    $[119] = t31;
    $[120] = t32;
    $[121] = t38;
    $[122] = t39;
  } else {
    t39 = $[122];
  }
  let t40;
  if ($[123] !== t30 || $[124] !== t39) {
    t40 = <>{t30}{t39}</>;
    $[123] = t30;
    $[124] = t39;
    $[125] = t40;
  } else {
    t40 = $[125];
  }
  return t40;
};
export { MultiSelectButtonOpener };
export default MultiSelectDropdown;
export { MultiSelectDropdown };
export type { MultiSelectDropdownItem } from './types';
function _temp() {
  return null;
}
function _temp2(t0) {
  const {
    placement
  } = t0;
  const isSidePlacement = placement.startsWith("right") || placement.startsWith("left");
  return [0, isSidePlacement ? SUBMENU_SIDE_OFFSET_PX : SUBMENU_GAP_PX];
}
function _temp3() {
  window.dispatchEvent(new Event("resize"));
}